import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { money, fmtDateTime } from './formatting'

// La tabla de historial y el modal de anulación, compartidos por los dos
// modales de historial (pagos en `PayablesPage`, cobros en `ReceivablesPage`).
// Que las filas anuladas se rendericen en un único sitio es lo que hace que su
// presentación no se desincronice entre las dos páginas.

// `voided_by` existe en el modelo del backend (financial/models.py) pero
// PaymentOut/CollectionOut todavía no lo serializan — queda pendiente en ese
// repo. Lo leemos ya, en las dos formas plausibles de serialización (campo
// plano o objeto anidado), para que el día que se exponga el autor aparezca
// sin tocar este componente. Un id numérico a secas no se muestra: no es
// legible para nadie.
const voidedAuthor = (row) => row?.voided_by_name ?? row?.voided_by?.name ?? null

function AnnulledMark({ row }) {
  const author = voidedAuthor(row)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
      <span className="badge badge-gray" title={row.voided_at ? `Annulled ${fmtDateTime(row.voided_at)}` : 'Annulled'}>
        Annulled
      </span>
      {row.void_reason && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.void_reason}</span>
      )}
      {author && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>by {author}</span>
      )}
    </div>
  )
}

// Mismas columnas para pagos y cobros: sólo cambia el nombre del campo de fecha.
export function MovementHistoryTable({ rows, dateField, onEdit, onVoid }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Date</th><th>Amount</th><th>Reference</th><th>Note</th><th style={{ width: 90 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.is_voided ? { opacity: 0.55 } : undefined}>
              <td>
                {r[dateField]}
                {r.is_voided && <AnnulledMark row={r} />}
              </td>
              <td style={{ fontWeight: 600, textDecoration: r.is_voided ? 'line-through' : 'none' }}>
                {money(r.amount)}
              </td>
              <td style={{ color: 'var(--text-muted)' }}>{r.reference || '—'}</td>
              <td style={{ color: 'var(--text-muted)' }}>{r.note || '—'}</td>
              <td>
                {/* Una fila anulada no lleva acciones: el backend contesta 409
                    tanto a editarla (VoidedMovementImmutable) como a volver a
                    anularla (AlreadyVoided). Ofrecer los botones sería una
                    trampa. */}
                {!r.is_voided && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={() => onEdit(r)}>✎</button>
                    <button className="btn btn-danger btn-icon btn-sm" title="Annul" onClick={() => onVoid(r)}>🚫</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function VoidConfirmModal({ noun, onCancel, onConfirm }) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    const r = reason.trim()
    // El backend acepta `reason` vacío, pero una anulación sin motivo es la
    // mitad de un registro de auditoría: la exigimos aquí.
    if (!r) { toast.error('Enter a reason for annulling'); return }
    setBusy(true)
    try { await onConfirm(r) } finally { setBusy(false) }
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="card fin-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Annul {noun}?</h3>
        <p style={{ color: 'var(--text-muted)' }}>
          The {noun} stays in the history for the audit trail and stops counting toward the
          balance. It can&apos;t be edited or annulled again afterwards.
        </p>
        <div>
          <label className="form-label">Reason</label>
          <input className="input fin-input" type="text" autoFocus
            placeholder={`Why is this ${noun} being annulled?`}
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={submit} disabled={busy}>
            {busy ? 'Annulling…' : 'Annul'}
          </button>
        </div>
      </div>
    </div>
  )
}
